import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import DialogTransition from './DialogTransition';
import { useAppSelector } from '../../hooks/useAppSelector';
import * as userSlice from '../../data/user.slice';

const PREFIX = 'EulaDialog';

const classes = {
  appBar: `${PREFIX}-appBar`,
  title: `${PREFIX}-title`,
  content: `${PREFIX}-content`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.appBar}`]: {
    position: 'relative',
  },

  [`& .${classes.title}`]: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },

  [`& .${classes.content}`]: {
    border: 'none',
    width: '100%',
    height: '100%',
  },
}));

interface EulaDialogProps {
  open: boolean;
  close: (version: number | null) => void;
}

export default function EulaDialog({ open, close }: EulaDialogProps) {
  const { t } = useTranslation('common');
  const eulaInfo = useAppSelector(userSlice.selectEULAInfo);

  const [html, setHtml] = React.useState<string>();
  const [eulaVersion, setEulaVersion] = React.useState<number | null>(null);
  const [isRead, setIsRead] = React.useState(false);

  React.useEffect(() => {
    if (eulaInfo) {
      setEulaVersion(eulaInfo?.supportAgreementID);
      fetch(eulaInfo?.supportAgreementFileNameWithPath).then((resp: any) =>
        resp.text().then((html: string) => setHtml(html)),
      );
    }
  }, [eulaInfo]);

  const handleCancel = () => {
    close(null);
  };

  const handleAgree = () => {
    close(eulaVersion);
  };

  const handleScroll = (evt: any) => {
    const elt = evt.target;
    const scrollPos = elt.offsetHeight + elt.scrollTop;
    if (scrollPos >= elt.scrollHeight - 50) {
      setIsRead(true);
    }
  };

  if (!html) {
    return null;
  }

  return (
    <StyledDialog
      open={open}
      onClose={handleCancel}
      TransitionComponent={DialogTransition}
      scroll="paper"
      maxWidth="md">
      <DialogTitle>{t('eula.title')}</DialogTitle>
      <DialogContent onScroll={handleScroll}>
        <div
          className={classes.content}
          dangerouslySetInnerHTML={{ __html: html || '' }}></div>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleCancel}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!isRead}
          onClick={handleAgree}>
          {t('eula.agree')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
